import React, { useRef, useState, useEffect } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/Modal/Modal';
import Button from '@/components/forms/Button/Button';
import styles from './BillingInformationModal.module.css';
import { CardElement, useRecurly } from '@recurly/react-recurly';
import { US_STATES, CANADA_PROVINCES, TOP_COUNTRIES, OTHER_COUNTRIES } from '@/utils/geo-data';
import Flex from '@/components/Flex/Flex';
import { updatePaymentMethod } from '@/api/billing';
import { PaymentMethodUpdateErrorType } from '@boxem/core-api-schema';
import { useThemeDetector } from '@/hooks/useThemeDetector';

export interface BillingInformationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose?: () => void;
  onSuccess?: () => void;
  billingInfoId?: string;
  initialData?: {
    firstName: string;
    lastName: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

export default function BillingInformationModal({
  open,
  onOpenChange,
  onClose,
  onSuccess,
  billingInfoId,
  initialData
}: BillingInformationModalProps) {
  const recurly = useRecurly();
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDarkTheme = useThemeDetector();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formRef.current) {
      setLoading(false);
      return;
    }

    if (!billingInfoId) {
      setError('Billing information ID is required');
      setLoading(false);
      return;
    }

    const formElement = formRef.current;

    recurly.token(formElement, async (err, token) => {
      if (err) {
        console.error('Recurly token error:', err);
        setError(err.message || 'Failed to process payment information');
        setLoading(false);
        return;
      }

      try {
        // Call the updatePaymentMethod API
        await updatePaymentMethod({
          recurlyTokenId: token.id,
          billingInfoId: billingInfoId!
        });
        
        onSuccess?.();
        onOpenChange(false);
      } catch (error) {
        if (error.status === 422 && error.type === PaymentMethodUpdateErrorType.PAYMENT_DECLINED) {
          console.error('Payment Declined:', error);
          setError("Payment Declined. Please Contact your Bank");
          return;
        }

        console.error('Error updating payment method:', error);
        setError('Failed to update payment method. Please try again.');
      } finally {
        setLoading(false);
      }
    });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      onClose={onClose}
      className={styles.modal}
      style={{}}
    >
      <form ref={formRef} onSubmit={onSubmit}>
        <ModalHeader divider={false} style={{}}>
          <h2 className={styles.title}>Billing Information</h2>
        </ModalHeader>
        
        <ModalBody className={styles.modalBody}>

          <div className={styles.grid}>
            <div className={styles.formGroup}>
              <label htmlFor="first_name">
                  First Name <span className={styles.required}>*</span>
                </label>
                <input 
                  id="first_name"
                  name="first_name" 
                  data-recurly="first_name" 
                  defaultValue={initialData?.firstName}
                  required
                  className={styles.input1}
                />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="last_name">
                  Last Name <span className={styles.required}>*</span>
                </label>
                <input 
                  id="last_name"
                  name="last_name" 
                  data-recurly="last_name" 
                  defaultValue={initialData?.lastName}
                  required
                  className={styles.input1}
                />
            </div>
            <div className={`${styles.formGroup} ${styles.inputRowFull}`}>
              <label htmlFor="card">
                Credit Card Number:
              </label>
              <CardElement
                id="card"
                className={styles.cardElement}
                style={{
                  fontSize: "16px",
                  fontFamily: "Inter",
                  fontColor: isDarkTheme ? '#FFFFFF' : '#1A1A1A;'
                }}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="country">
                Country <span className={styles.required}>*</span>
              </label>
              <select 
                id="country"
                name="country" 
                data-recurly="country" 
                defaultValue={initialData?.country || "US"}
                required
                className={styles.input1}
              >
                <optgroup label="Common Countries">
                  {TOP_COUNTRIES.map(country => (
                    <option key={country.Code} value={country.Code}>{country.Name}</option>
                  ))}
                </optgroup>
                <optgroup label="Other Countries">
                  {OTHER_COUNTRIES.map(country => (
                    <option key={country.Code} value={country.Code}>{country.Name}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div>
            </div>
            
            <div className={styles.formGroup}>
                <label htmlFor="address1">
                  Street Address 1 <span className={styles.required}>*</span>
                </label>
                <input 
                  id="address1"
                  name="address1" 
                  data-recurly="address1" 
                  defaultValue={initialData?.address1}
                  required
                  className={styles.input1}
                />
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="address2">
                  Street Address 2
                </label>
                <input 
                  id="address2"
                  name="address2" 
                  data-recurly="address2" 
                  defaultValue={initialData?.address2}
                  className={styles.input1}
                />  
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="city">
                  City <span className={styles.required}>*</span>
                </label>
                <input 
                  id="city"
                  name="city" 
                  data-recurly="city" 
                  defaultValue={initialData?.city}
                  required
                  className={styles.input1}
                />
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="state">
                  State/Province <span className={styles.required}>*</span>
                </label>
                <select 
                  id="state"
                  name="state" 
                  data-recurly="state" 
                  defaultValue={initialData?.state}
                  required
                  className={styles.input1}
                >
                  <optgroup label="US States">
                    {US_STATES.map((state, index) => (
                      <option key={index} value={state}>{state}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Canadian Provinces">
                    {CANADA_PROVINCES.map((province, index) => (
                      <option key={index} value={province}>{province}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Other">
                    <option value="Other">Other</option>
                  </optgroup>
                </select>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="postal_code">
                  Zip/Postal Code <span className={styles.required}>*</span>
                </label>
                <input 
                  id="postal_code"
                  name="postal_code" 
                  data-recurly="postal_code"  
                  defaultValue={initialData?.postalCode}
                  required
                  className={styles.input1}
                />
            </div>
          </div>
          {error && <div className={styles.error}>{error}</div>}
        </ModalBody>
        
        <ModalFooter style={{}}>
          <Flex justify='end' style={{ width: '100%' }}  align='center' gap={2} >
              <Button 
                type="secondary" 
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                disabled={loading}
              >
                Update
              </Button>
           </Flex>
        </ModalFooter>
          
      </form>
    </Modal>
  );
}
